import { useEffect, useState } from 'react';
import { useStyletron } from 'baseui'
import { Button, KIND } from 'baseui/button';
import { ButtonGroup, SIZE, SHAPE, MODE } from 'baseui/button-group';
import { Block } from 'baseui/block';
import { IScheduler, ViewModes } from './types'
import { ISchedulerEvent, convertWeeklySchedule, convertWeeklyScheduleBack } from './weeklyScheduleConverter';
import { VscAdd, VscListOrdered } from 'react-icons/vsc';
import { IoCalendarOutline, IoTodayOutline } from 'react-icons/io5';
import { useIntl, FormattedMessage } from 'react-intl';
import useIsMobile from '../../../hooks/useIsMobile/useIsMobile';
import { OperatorPanelWrapperFullSize } from '../../components/Layout';
import { ExceptionsView } from './Exceptions';
import { MdOutlineEditCalendar } from 'react-icons/md';
import { useGetExceptionScheduleQuery, useGetWeeklyScheduleQuery, useUpdateWeeklyScheduleMutation } from '../../../services/schedulers';
import { UpdateWeeklyScheduleArgs } from '../../../services/schedulers/types';
import { useReportMutationState } from '../../../hooks/useReportMutationState';
import { EditableScheduler } from './EditableScheduler';
import { StateText } from './StateText';
import { Banner, KIND as KINDBanner } from 'baseui/banner';
import { StyledLink } from 'baseui/link';
import { Link } from 'react-router-dom';
import { GetObjectUrl } from '../../../utils/navigationService';
import { NotificationCircle } from 'baseui/badge';


const Scheduler = (props: IScheduler) => {
  const [css, theme] = useStyletron();

  const intl = useIntl();
  const isMobile = useIsMobile();

  const [hasChanges, setHasChanges] = useState(false);
  const [view, setView] = useState(isMobile ? ViewModes.listWeek : ViewModes.timeGridWeek);
  const [events, setEvents] = useState<ISchedulerEvent[]>([]);

  const { data: weeklySchedule2, refetch } = useGetWeeklyScheduleQuery({ resourceGroupId: props.resourceGroupId, objectId: props.id });
  const { data: exceptionSchedule1 } = useGetExceptionScheduleQuery({ resourceGroupId: props.resourceGroupId, objectId: props.id });
  const [update, upateResult] = useUpdateWeeklyScheduleMutation();
  useReportMutationState(upateResult);

  useEffect(() => {
    if (isMobile)
      setView(ViewModes.listWeek)
  }, [isMobile]);

  useEffect(() => {
    const entries = convertWeeklySchedule(weeklySchedule2, theme);
    setEvents(entries ?? []);
  }, [weeklySchedule2, view]);

  useEffect(() => {
    setHasChanges(false);
    refetch();
  }, [upateResult.isSuccess]);

  const exceptionsSchedCount = exceptionSchedule1?.length ?? 0;
  return (
    <>

      <OperatorPanelWrapperFullSize
        objectId={props.id}
        resourceGroupId={props.resourceGroupId}
        statusContent={
          <div className={css({ alignSelf: 'left' })}>
            <StateText  {...props}></StateText>
          </div>
        }
        operationModeContent={
          <>
            {(!props.timeZone || props.timeZone === 'UTC') && props.schema === 'AutomationApps.Functions.Schedules.ControlledScheduler' &&
              <Banner kind={KINDBanner.warning}>
                <FormattedMessage id="Scheduler.NotInitializedTimezone" defaultMessage="Timezone configuration is missing." />
                <span> </span>
                <Link to={`${GetObjectUrl(props.resourceGroupId, props.id)}?defaultView=configuration`} className={css({ textDecoration: 'none' })}>
                  <StyledLink animateUnderline ><FormattedMessage id="configuration" defaultMessage="Configuration" /></StyledLink>
                </Link>
              </Banner>}</>
        }
        additionalContent={
          <Block height='100%' width='100%' flexDirection='column' display='flex' alignContent='space-between' gridGap='0'>
            <Block flexDirection='row' width='100%' display='flex' justifyContent='space-between' alignItems='baseline'>
              <ButtonGroup kind='tertiary' size={SIZE.compact} >
                <Button kind='tertiary' isSelected={view === ViewModes.timeGridWeek} onClick={() => setView(ViewModes.timeGridWeek)} title={intl.formatMessage({ id: "Scheduler.ShowWeek", defaultMessage: "show Week" })}><IoCalendarOutline /></Button>
                <Button kind='tertiary' isSelected={view === ViewModes.listWeek} onClick={() => setView(ViewModes.listWeek)} title={intl.formatMessage({ id: "Scheduler.ShowList", defaultMessage: "show List" })}><VscListOrdered /></Button>
                <NotificationCircle
                  content={exceptionsSchedCount}
                  hidden={!exceptionsSchedCount}
                  color={exceptionsSchedCount ? 'accent' : 'primary'}
                  placement={'topRight'}
                  horizontalOffset="0px"
                  verticalOffset="0px"
                >
                  <Button kind='tertiary' isSelected={view === ViewModes.exceptions} onClick={() => setView(ViewModes.exceptions)} title={intl.formatMessage({ id: "Scheduler.ShowExceptions", defaultMessage: "show Exceptions" })}><MdOutlineEditCalendar /></Button>
                </NotificationCircle>

              </ButtonGroup>
              {hasChanges && <div className={css({ alignSelf: 'flex-end' })}><Button size='compact' isLoading={upateResult.isLoading} onClick={onApplyChanges}><FormattedMessage id="applyChanges" defaultMessage="Apply changes" /></Button></div>}
            </Block>
            {view === ViewModes.exceptions && <ExceptionsView {...props}></ExceptionsView>}
            {view !== ViewModes.exceptions &&

              <EditableScheduler onEventsChanged={handleEventsChanged} app={props} dataType={props.dataType} events={events} view={view} ></EditableScheduler>
            }
          </Block>
        }>
      </OperatorPanelWrapperFullSize>


    </>
  );

  function handleEventsChanged(events) {
    setEvents(events);
    setHasChanges(true);
  }

  function onApplyChanges() {
    const newval = convertWeeklyScheduleBack(events);
    const cmd: UpdateWeeklyScheduleArgs = {
      resourceGroupId: props.resourceGroupId,
      objectId: props.id,
      body: newval
    }
    update(cmd);
  }


}

export default Scheduler;

